import type { BillingPreviewUI } from "@/domain/billing/billing-preview.model";
import type { CartPricingGateContext, CartPricingFreshness, CartPricingTier } from "@/domain/cart/cart-validity.types";
import type { PricingConfirmation } from "@/domain/pricing/pricing-confirmation";
import type { PricingStatus } from "@/domain/pricing/pricing-preview.model";
import type { CartLine } from "@/shared/cart/model.types";

export type BuildCartPricingGateInput = {
  cartLines: CartLine[];
  billingPreview?: BillingPreviewUI | null;
  pricingStatus: PricingStatus;
  isSyncing: boolean;
  isUsingStalePricing: boolean;
  liveFingerprint: string;
  committedFingerprint: string;
  catalogFingerprint?: string;
  /**
   * Owned BackendConfirmation from the sync boundary.
   * Required for PREVIEW_AUTHORITATIVE / Place Order — memo hydrate alone is insufficient.
   */
  pricingConfirmation?: PricingConfirmation | null;
};

function confirmationMatchesLiveEpoch(input: BuildCartPricingGateInput): boolean {
  const confirmation = input.pricingConfirmation;
  if (!confirmation) return false;
  return confirmation.epoch === input.liveFingerprint;
}

function resolveFreshness(input: BuildCartPricingGateInput): CartPricingFreshness {
  if (!input.cartLines.length) return "missing";
  if (input.isUsingStalePricing) return "stale";
  if (input.isSyncing || input.pricingStatus === "syncing") return "syncing";
  // Display payload without live-epoch confirmation is not Place-Order-fresh (memo / prior epoch).
  if (input.billingPreview && confirmationMatchesLiveEpoch(input)) return "fresh";
  if (input.billingPreview) return "syncing";
  return "missing";
}

function resolveTier(input: BuildCartPricingGateInput): CartPricingTier {
  // ADR Phase 3: authoritative only with Pricing Confirmation for the live epoch —
  // not pricingStatus === "confirmed" or payload presence alone.
  if (
    input.billingPreview &&
    !input.isSyncing &&
    confirmationMatchesLiveEpoch(input)
  ) {
    return "PREVIEW_AUTHORITATIVE";
  }
  if (input.billingPreview) return "PREVIEW_ESTIMATE";
  return "LINE_ESTIMATE";
}

/** Builds pricing gate context for checkout boundaries (separate from validity resolution). */
export function buildCartPricingGateContext(input: BuildCartPricingGateInput): CartPricingGateContext {
  const fingerprint = input.committedFingerprint || input.liveFingerprint;
  const confirmation = input.pricingConfirmation ?? null;
  return {
    tier: resolveTier(input),
    freshness: resolveFreshness(input),
    fingerprint,
    liveFingerprint: input.liveFingerprint,
    hasCartItems: input.cartLines.length > 0,
    preview: input.billingPreview
      ? { payableAmount: input.billingPreview.payableAmount }
      : null,
    catalogFingerprint: input.catalogFingerprint,
    confirmationEpoch: confirmation?.epoch ?? null,
  };
}

/** Minimal pricing context for cart-page checkout entry (validity gate only). */
export function buildCartPagePricingGateContext(cartLines: CartLine[]): CartPricingGateContext {
  return {
    tier: "LINE_ESTIMATE",
    freshness: "missing",
    fingerprint: "",
    liveFingerprint: "",
    hasCartItems: cartLines.length > 0,
    preview: null,
    confirmationEpoch: null,
  };
}
